import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { Alert, Button, Icon, Loading, Modal, Typography } from "@/components/atoms";
import { AddCardForm } from "@/components/organisms/Onboarding/AddCardForm";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { useOperator } from "@/hooks";
import { StripePaymentMethod } from "@/redux/apis/payment/stripe/types";
import {
  useDeleteProviderPaymentMethodMutation,
  useGetProviderPaymentMethodsQuery,
  useUpdateProviderPaymentMethodMutation,
} from "@/redux/apis/subscription/subscriptionPlansApi";
import { addToast } from "@/utils";
import { PaymentMethodResultFeedback, describePaymentMethodResult } from "./paymentMethodResult";

interface ChangePaymentMethodModalProps {
  open: boolean;
  onClose: () => void;
  initialShowAddCard?: boolean;
}

// Helper to get card brand icon name
const _getCardBrandIcon = (brand: string): string => {
  const brandIcons: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "Amex",
    discover: "Discover",
  };
  return brandIcons[brand.toLowerCase()] || "Card";
};

// Helper to format card brand for display
const formatCardBrand = (brand: string): string => {
  return brand.charAt(0).toUpperCase() + brand.slice(1);
};

interface PaymentMethodCardProps {
  paymentMethod: StripePaymentMethod;
  onSetDefault: () => void;
  isSettingDefault: boolean;
  /** Remove is a two-step: "Remove" asks, "Yes, remove" does it. */
  confirmingRemove: boolean;
  onRemove: () => void;
  onConfirmRemove: () => void;
  onCancelRemove: () => void;
  isRemoving: boolean;
}

// The default card is what renewals charge, so it can't be removed; make
// another card the default first.
const PaymentMethodCard = ({
  paymentMethod,
  onSetDefault,
  isSettingDefault,
  confirmingRemove,
  onRemove,
  onConfirmRemove,
  onCancelRemove,
  isRemoving,
}: PaymentMethodCardProps) => {
  const { card, isDefault } = paymentMethod;
  const busy = isSettingDefault || isRemoving;

  return (
    <div className="border-neutral-light-gray flex w-full items-center gap-3 rounded-lg border p-4">
      <Icon name="Card" size="lg" className="text-neutral-dark-gray" />
      <div className="flex flex-1 flex-col">
        <Typography variant="paragraph">
          {formatCardBrand(card.brand)} ending in {card.last4}
        </Typography>
        <Typography variant="small" className="text-neutral-dark-gray">
          Expires {card.expMonth.toString().padStart(2, "0")}/{card.expYear}
        </Typography>
      </div>
      {!isDefault &&
        (confirmingRemove ? (
          <div className="flex items-center gap-2">
            <Typography variant="small" className="text-neutral-dark-gray">
              Remove this card?
            </Typography>
            <Button variant="secondary" size="sm" onClick={onConfirmRemove} disabled={busy}>
              {isRemoving ? "Removing..." : "Yes, remove"}
            </Button>
            <Button variant="tertiary-light" size="sm" onClick={onCancelRemove} disabled={busy}>
              Keep
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Button variant="tertiary" size="sm" onClick={onSetDefault} disabled={busy}>
              {isSettingDefault ? "Setting..." : "Set as Default"}
            </Button>
            <Button variant="tertiary-light" size="sm" onClick={onRemove} disabled={busy}>
              Remove
            </Button>
          </div>
        ))}
    </div>
  );
};

export const ChangePaymentMethodModal = ({ open, onClose, initialShowAddCard = false }: ChangePaymentMethodModalProps) => {
  const operator = useOperator();
  const [showAddCard, setShowAddCard] = useState(initialShowAddCard);
  const [error, setError] = useState<string | null>(null);
  // How paying the overdue invoice with the new card went, when it needs attention
  const [resultAlert, setResultAlert] = useState<PaymentMethodResultFeedback["alert"] | null>(null);
  // The card the last result was about, so "Try again" can re-apply it
  const [lastPaymentMethodId, setLastPaymentMethodId] = useState<string | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<string | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  // Sync showAddCard when modal opens with initialShowAddCard=true
  // (useState only captures the initial value on first render)
  useEffect(() => {
    if (open && initialShowAddCard) {
      setShowAddCard(true);
    }
  }, [open, initialShowAddCard]);

  const {
    data: paymentMethodsData,
    isLoading: isLoadingPaymentMethods,
    refetch,
  } = useGetProviderPaymentMethodsQuery({ providerId: operator.id });

  const [updatePaymentMethod] = useUpdateProviderPaymentMethodMutation();
  const [deletePaymentMethod] = useDeleteProviderPaymentMethodMutation();

  const paymentMethods = paymentMethodsData || [];

  const handleCancel = () => {
    setShowAddCard(false);
    setError(null);
    setResultAlert(null);
    setConfirmRemoveId(null);
    onClose();
  };

  // The backend also pays any overdue invoice with the new card; a decline or a
  // 3DS challenge comes back as a result, not an error, so show it here.
  const showResult = (result: Parameters<typeof describePaymentMethodResult>[0], addedCard: boolean) => {
    const feedback = describePaymentMethodResult(result, { addedCard });
    if (feedback.toast) addToast(feedback.toast.type, feedback.toast.message);
    setResultAlert(feedback.alert ?? null);
    setLastPaymentMethodId(result.paymentMethodId);
  };

  const handleSetDefault = async (paymentMethodId: string) => {
    setSettingDefaultId(paymentMethodId);
    setError(null);
    setResultAlert(null);

    try {
      const result = await updatePaymentMethod({
        providerId: operator.id,
        paymentMethodId,
        isDefault: true,
      }).unwrap();

      showResult(result, false);
    } catch (err) {
      const errors = getErrorMessages(err as FetchBaseQueryError | SerializedError);
      setError(errors[0] || "Failed to update payment method");
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Detaches the card from the provider's Stripe customer (non-default cards only,
  // the card itself stays on the operator's bank side). The list refetches via tags.
  const handleRemove = async (paymentMethodId: string) => {
    setRemovingId(paymentMethodId);
    setError(null);
    setResultAlert(null);

    try {
      await deletePaymentMethod({ providerId: operator.id, paymentMethodId }).unwrap();
      addToast("success", "Card removed");
    } catch (err) {
      const errors = getErrorMessages(err as FetchBaseQueryError | SerializedError);
      setError(errors[0] || "Failed to remove card");
    } finally {
      setRemovingId(null);
      setConfirmRemoveId(null);
    }
  };

  const handleCardAdded = async (paymentMethodId: string) => {
    setShowAddCard(false);
    setError(null);
    setResultAlert(null);

    try {
      const result = await updatePaymentMethod({
        providerId: operator.id,
        paymentMethodId: paymentMethodId,
        isDefault: true,
      }).unwrap();

      showResult(result, true);
      refetch();
    } catch (err) {
      const errors = getErrorMessages(err as FetchBaseQueryError | SerializedError);
      setError(errors[0] || "Failed to set payment method as default");
    }
  };

  const handleAddCardCancel = () => {
    setShowAddCard(false);
  };

  return (
    <Modal open={open} onClose={handleCancel} className="w-[85vw] md:w-[500px] lg:w-[500px]">
      <div className="flex flex-col">
        {/* Title and Description */}
        <div className="flex flex-col gap-2">
          <Typography variant="title">Change Payment Method</Typography>
          <Typography variant="paragraph" className="text-neutral-dark-gray">
            {showAddCard ? "Fill up the credit card information" : "Select a payment method or add a new one"}
          </Typography>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mt-4">
            <Alert type="danger" message={error} />
          </div>
        )}

        {/* Overdue-invoice outcome that needs the operator's attention */}
        {resultAlert && (
          <div className="mt-4">
            <Alert
              type={resultAlert.type}
              role={resultAlert.type === "warning" ? "alert" : "status"}
              message={
                <span>
                  {resultAlert.message}
                  {resultAlert.link && (
                    <>
                      {" "}
                      <a href={resultAlert.link.href} target="_blank" rel="noopener noreferrer" className="font-semibold underline">
                        {resultAlert.link.label}
                      </a>
                    </>
                  )}
                  {resultAlert.retry && lastPaymentMethodId && (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="font-semibold underline"
                        disabled={settingDefaultId !== null}
                        onClick={() => handleSetDefault(lastPaymentMethodId)}
                      >
                        {settingDefaultId ? "Trying again..." : "Try again"}
                      </button>
                    </>
                  )}
                </span>
              }
            />
          </div>
        )}

        {/* Content Section */}
        <div className="flex flex-col gap-4 py-6">
          {isLoadingPaymentMethods ? (
            <div className="relative min-h-[150px]">
              <Loading className="rounded-lg" />
            </div>
          ) : showAddCard ? (
            <AddCardForm providerId={operator.id} countryCode={operator.country} onCardAdded={handleCardAdded} onCancel={handleAddCardCancel} />
          ) : (
            <>
              {/* Existing Payment Methods */}
              {paymentMethods.length > 0 && (
                <div className="flex flex-col gap-3">
                  {paymentMethods.map((pm) => (
                    <PaymentMethodCard
                      key={pm.paymentMethodId}
                      paymentMethod={pm}
                      onSetDefault={() => handleSetDefault(pm.paymentMethodId)}
                      isSettingDefault={settingDefaultId === pm.paymentMethodId}
                      confirmingRemove={confirmRemoveId === pm.paymentMethodId}
                      onRemove={() => setConfirmRemoveId(pm.paymentMethodId)}
                      onConfirmRemove={() => handleRemove(pm.paymentMethodId)}
                      onCancelRemove={() => setConfirmRemoveId(null)}
                      isRemoving={removingId === pm.paymentMethodId}
                    />
                  ))}
                  <Typography variant="small" className="text-neutral-dark-gray">
                    Your default card can&apos;t be removed. To remove it, make another card the default first.
                  </Typography>
                </div>
              )}

              {/* Add New Card Button */}
              <button
                type="button"
                onClick={() => {
                  setError(null);
                  setShowAddCard(true);
                }}
                className="flex w-full items-center gap-3 rounded-lg border border-dashed border-neutral-dark-gray p-4 text-left transition-colors hover:bg-neutral-surface-gray"
              >
                <Icon name="Add" size="lg" className="text-primary" />
                <Typography variant="paragraph" className="text-primary">
                  Add new payment method
                </Typography>
              </button>
            </>
          )}
        </div>

        {/* Action Buttons - Only show when not adding a card (AddCardForm has its own buttons) */}
        {!showAddCard && (
          <div className="mt-8 flex flex-row justify-end">
            <Button type="button" size="lg" variant="secondary" onClick={handleCancel}>
              Close
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};
